import { format } from "date-fns";
import { fullDateFullYearFormat, timeFormat } from "@common/helpers/date";
import { CellText, EmptyCellValue } from "@design-system/DataGrid";
import {
    type TaskListCell,
    type TaskListTaskRow,
    toReadOnlyTaskCell,
} from "./taskRowCells";

type TaskTimestamp = (task: TaskListTaskRow) => Date | null | undefined;

/**
 * The day in the viewer's zone, as today's `CreatedAtCell`: rows carry no
 * workspace time zone yet (PRO-13213, `docs/specs/dates-and-timezones.md`).
 */
const toTaskTimestampCell = (getTimestamp: TaskTimestamp): TaskListCell => {
    const TaskTimestampValue = ({ task }: { task: TaskListTaskRow }) => {
        const timestamp = getTimestamp(task);
        return timestamp ? (
            <CellText
                text={format(timestamp, fullDateFullYearFormat)}
                overflow="truncate"
                tooltip={format(timestamp, timeFormat)}
            />
        ) : (
            <EmptyCellValue empty="dash" />
        );
    };
    return toReadOnlyTaskCell(TaskTimestampValue);
};

/** The Created on column: the day the task was created, its time in a tooltip. */
export const TaskCreatedAtCell = toTaskTimestampCell((task) => task.createdAt);

/** The Updated on column: the day the task last changed, its time in a tooltip. */
export const TaskUpdatedAtCell = toTaskTimestampCell((task) => task.updatedAt);

/** The Completed on column: the day the task was last done, its time in a tooltip. */
export const TaskCompletedAtCell = toTaskTimestampCell(
    (task) => task.lastDoneAt,
);
